<script setup lang="ts">
/**
 * Small-screen nav control. Below --md the SideNav is parked offscreen
 * (translateX(-100%)) and only `.drawerOpen` brings it back, so without this
 * button the nav is unreachable, not merely collapsed.
 *
 * Renders nothing at --md and up, where the nav sits in the grid rail and the
 * pin/hover affordances take over.
 *
 * Provided by App.vue (PageFrame) and injected by page headers — see
 * ./nav-toggle for why the frame owns the state.
 *
 * Relative (not @/) imports: this is reachable from the ./layout expose, whose
 * emitted d.ts ships to consumers where the producer's @/ alias can't resolve.
 */
import { computed } from "vue";
import Button from "primevue/button";
import { useSideNav } from "./useSideNav";

const { isSmallScreen, isSmallScreenDrawerOpen, toggleSmallScreenDrawer } = useSideNav();

/**
 * Tooltip must switch OFF while the drawer is open, or a tap leaves it stranded
 * on top of the nav it just opened.
 *
 * Why a tap specifically: PrimeVue's tooltip binds `mouseenter` (show) and
 * `click` (hide), and Chromium synthesises BOTH on a tap — in that order, with
 * the show deferred through a `setTimeout`. So `click` runs its hide before the
 * show timer has created anything, then the timer fires and builds the tooltip
 * with no pointer left on the button to ever dismiss it. Measured order on tap:
 * mouseenter 50ms, click 51ms, tooltip node created 57ms.
 *
 * `disabled` is the fix because `tooltipActions()` — the deferred show itself —
 * early-returns on `el.$_ptooltipDisabled`, so the late timer produces nothing.
 * It has to be the OBJECT form: with a plain string value, the directive's
 * `updated` hook takes a branch that never reads `disabled` (and never checks for
 * an empty value either, so blanking the string does nothing but empty the text).
 * `disabled` is absent from the published options table but is honoured in the
 * source; covered by the touch/mouse/keyboard checks noted on this component.
 */
const tooltip = computed(() => ({
  value: "Open navigation",
  disabled: isSmallScreenDrawerOpen.value,
}));

// The drawer covers the button once open, so nothing should keep focus on it.
// Also stops the focus-triggered tooltip re-appearing after the click.
function onClick(event: MouseEvent) {
  toggleSmallScreenDrawer();
  (event.currentTarget as HTMLElement | null)?.blur();
}
</script>

<template>
  <Button
    v-if="isSmallScreen"
    v-tooltip.bottom="tooltip"
    severity="secondary"
    text
    rounded
    size="small"
    aria-label="Open navigation"
    aria-controls="app-sidenav"
    :aria-expanded="isSmallScreenDrawerOpen"
    @click="onClick"
  >
    <template #icon>
      <span class="material-icons-round" data-size="sm" aria-hidden="true">menu</span>
    </template>
  </Button>
</template>
